import { getSummary } from '@/components/Company/redux/selectors';
import useWatchlist from '@/components/Watchlist/hooks/useWatchlist';
import useWatchlistMutations from '@/components/Watchlist/hooks/useWatchlistMutations';
import {
  USERPILOT_CLASSIFICATION,
  USERPILOT_CONFIRMATION,
} from '@/constants/featureFlags';
import { useFlags } from '@/hooks/useFlags';
import useUser from '@/hooks/useUser';
import { getUi } from '@/redux/ui/selectors';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTracking } from 'react-tracking';
import { useUserpilot } from '../useUserpilot';

/**
Confirmation Experiment

TLDR: A Confirmation modal asking whether the user is happy to
opt into monitoring and add the current company to their Watchlist.
Auto-adding to Watchlist is removed as part of this experiment.

Details: https://simplywallst.atlassian.net/browse/GROWTH-567

https://run.userpilot.io/flows/32/settings
 */

export const ConfirmationExperiment = ({
  companyId,
}: {
  companyId: string;
}) => {
  const [userpilot] = useUserpilot();
  const confirmationFlag = useFlags(USERPILOT_CONFIRMATION.KEY);
  const classificationFlag = useFlags(USERPILOT_CLASSIFICATION.KEY);
  const { trackEvent } = useTracking();
  const ui = useSelector(getUi);
  const { data: user } = useUser();
  const summary = useSelector((state) => getSummary(state, { companyId }));
  const { addCompany } = useWatchlistMutations();
  const { id: watchlistId } = useWatchlist();
  const hasRun = useRef(false);

  const trackFn = useCallback(
    (eventName, data) => {
      const formattedName = data.name.split(' ').join('_').toLowerCase();
      trackEvent({
        type: 'track',
        subject: `userpilot.${formattedName}`,
        action: eventName,
        ...data,
      });
    },
    [trackEvent]
  );

  const shouldRunConfirmExperiment =
    !ui.showOnboarding &&
    confirmationFlag.status === 'success' &&
    confirmationFlag.variant === USERPILOT_CONFIRMATION.VARIANTS.TEST &&
    classificationFlag.status === 'success' &&
    classificationFlag.variant !==
      USERPILOT_CLASSIFICATION.VARIANTS.MONITORING_EXPERIMENT &&
    typeof summary !== 'undefined' &&
    typeof userpilot !== 'undefined';

  useEffect(() => {
    if (!shouldRunConfirmExperiment || hasRun.current) {
      return;
    }

    const timer = setTimeout(
      () => {
        hasRun.current = true;
        userpilot.once('completed', () => {
          trackEvent({
            type: 'track',
            subject: 'watchlist',
            action: 'add',
            context: 'modal',
          });
          addCompany({
            id: summary.companyId,
            includeSnackBar: true,
            watchlistId,
            autoAdded: false,
          });
        });
        userpilot.once('dismissed', ({ token, ...eventData }) => {
          trackFn('dismissed', eventData);
        });
        userpilot.identify(user.id, {
          showConfirmation: 'true',
          companyName: summary.name,
          created_at: new Date(user.registrationDate).toISOString(),
        });
      },
      40 * 1000 // 40 seconds delay
    );
    return () => clearTimeout(timer);
  }, [
    addCompany,
    shouldRunConfirmExperiment,
    summary,
    trackEvent,
    trackFn,
    user,
    userpilot,
    watchlistId,
  ]);

  return null;
};
